import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Icon,
} from 'semantic-ui-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
);

const MS_TO_KT = 0.539957;
const UV_ALERT_THRESHOLD = 3.0;

const chartAreaBandsPlugin = {
  id: 'chartAreaBands',
  beforeDatasetsDraw(chart, _args, pluginOptions) {
    const bands = pluginOptions?.bands;
    const yScale = chart.scales?.y;
    if (!bands?.length || !yScale || !chart.chartArea) return;

    const { ctx, chartArea } = chart;
    ctx.save();
    bands.forEach((band) => {
      const top = yScale.getPixelForValue(band.to);
      const bottom = yScale.getPixelForValue(band.from);
      ctx.fillStyle = band.color;
      ctx.fillRect(chartArea.left, top, chartArea.right - chartArea.left, bottom - top);
    });
    ctx.restore();
  },
};

const uvThresholdFillPlugin = {
  id: 'uvThresholdFill',
  beforeDatasetsDraw(chart, _args, pluginOptions) {
    const threshold = Number(pluginOptions?.threshold);
    if (!Number.isFinite(threshold)) return;

    const datasetIndex = chart.data?.datasets?.findIndex((dataset) => dataset.label === 'UV');
    if (datasetIndex == null || datasetIndex < 0) return;

    const yScale = chart.scales?.y;
    const meta = chart.getDatasetMeta(datasetIndex);
    const elements = meta?.data || [];
    const values = chart.data.datasets[datasetIndex]?.data || [];
    if (!yScale || elements.length < 2 || values.length < 2) return;

    const thresholdY = yScale.getPixelForValue(threshold);
    const { ctx } = chart;
    ctx.save();
    ctx.fillStyle = pluginOptions?.color || 'rgba(239, 68, 68, 0.20)';

    for (let index = 0; index < values.length - 1; index += 1) {
      const v0 = Number(values[index]);
      const v1 = Number(values[index + 1]);
      const p0 = elements[index];
      const p1 = elements[index + 1];
      if (!Number.isFinite(v0) || !Number.isFinite(v1) || !p0 || !p1) continue;
      if (v0 <= threshold && v1 <= threshold) continue;

      const x0 = p0.x;
      const x1 = p1.x;
      const y0 = yScale.getPixelForValue(v0);
      const y1 = yScale.getPixelForValue(v1);

      let startX = x0;
      let startY = y0;
      let endX = x1;
      let endY = y1;

      if (v0 < threshold && v1 > threshold) {
        const ratio = (threshold - v0) / (v1 - v0);
        startX = x0 + ((x1 - x0) * ratio);
        startY = thresholdY;
      } else if (v0 > threshold && v1 < threshold) {
        const ratio = (threshold - v0) / (v1 - v0);
        endX = x0 + ((x1 - x0) * ratio);
        endY = thresholdY;
      }

      ctx.beginPath();
      ctx.moveTo(startX, thresholdY);
      ctx.lineTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.lineTo(endX, thresholdY);
      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
  },
};

const safeArray = (value) => (Array.isArray(value) ? value : []);
const validNumbers = (values) => safeArray(values).map(Number).filter((value) => Number.isFinite(value));
const round = (value, digits = 0) => (Number.isFinite(value) ? Number(value.toFixed(digits)) : null);
const formatValue = (value, suffix = '', fallback = '-') => (
  Number.isFinite(value) ? `${round(value)}${suffix}` : fallback
);

const formatClock = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const formatBlockTimeRange = (block) => `${formatClock(block?.start)} - ${formatClock(block?.end)}`;

const getBlockItineraryMessages = (block, contexts = []) => {
  if (!block?.start || !block?.end || !Array.isArray(contexts)) return [];
  const overlaps = contexts
    .map((context) => ({
      ...context,
      start: context?.start instanceof Date ? context.start : new Date(context?.start),
      end: context?.end instanceof Date ? context.end : new Date(context?.end),
    }))
    .filter((context) => (
      !Number.isNaN(context.start.getTime())
      && !Number.isNaN(context.end.getTime())
      && context.start < block.end
      && context.end > block.start
    ))
    .sort((a, b) => a.start - b.start || a.end - b.end);
  if (!overlaps.length) return [];

  const describe = (context, { activeAtWindowStart = false } = {}) => {
    const isPort = context.type === 'port';
    const verb = isPort ? `In ${context.name}` : `${context.type === 'sailing' ? 'Sailing' : 'Travelling'} to ${context.toName}`;
    if (activeAtWindowStart && context.end < block.end) return `${verb} until ${formatClock(context.end)}`;
    if (!activeAtWindowStart && context.start > block.start) return `${verb} later`;
    return verb;
  };

  const activeContext = overlaps.find((context) => context.start <= block.start && context.end > block.start);
  const messages = activeContext ? [describe(activeContext, { activeAtWindowStart: true })] : [];
  const laterContext = overlaps.find((context) => context.start > block.start && context !== activeContext);
  if (laterContext) messages.push(describe(laterContext));
  return messages;
};

const getCardinalDirection = (degrees) => {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const number = Number(degrees);
  if (!Number.isFinite(number)) return '';
  return directions[Math.round(number / 22.5) % 16];
};

const getWindLevel = (kt) => {
  if (kt >= 25) return { label: 'very strong', color: '#dc2626' };
  if (kt >= 15) return { label: 'strong', color: '#ef4444' };
  if (kt >= 10) return { label: 'moderate', color: '#f97316' };
  return { label: 'light', color: '#16a34a' };
};

const getWeatherIcon = ({ rain = 0, cloud = 0, uv = 0 } = {}) => {
  if (rain >= 2) return { name: 'umbrella', color: '#0ea5e9' };
  if (cloud >= 80) return { name: 'cloud', color: '#64748b' };
  if (cloud >= 45) return { name: 'cloud sun', color: '#f59e0b' };
  if (uv >= 7) return { name: 'sun', color: '#f59e0b' };
  return { name: 'sun outline', color: '#f59e0b' };
};

const makeConditionLabel = ({ maxTemp, totalRain, avgCloud, maxUv }) => {
  if (totalRain >= 5) return 'Rain likely';
  if (totalRain >= 1) return 'Some rain possible';
  if (avgCloud >= 80) return 'Mostly cloudy';
  if (avgCloud >= 45) return maxTemp >= 28 ? 'Warm with some cloud' : 'Partly cloudy';
  if (maxTemp >= 30) return maxUv >= 7 ? 'Sunny and very warm' : 'Very warm';
  return 'Mostly sunny';
};

const buildHourlyPoints = (weatherData) => {
  const hourly = weatherData?.hourly || {};
  const times = safeArray(hourly.time);
  return times.map((timeValue, index) => {
    const date = new Date(timeValue);
    const windMps = Number(hourly.wind_speed_10m?.[index]);
    return {
      date,
      label: Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }),
      temp: Number(hourly.temperature_2m?.[index]),
      rain: Number(hourly.precipitation?.[index]),
      uv: Number(hourly.uv_index?.[index]),
      cloud: Number(hourly.cloud_cover?.[index]),
      windKt: Number.isFinite(windMps) ? windMps * MS_TO_KT : null,
      windDirection: Number(hourly.wind_direction_10m?.[index]),
      waveHeight: Number(hourly.wave_height?.[index] ?? hourly.wave_height_m?.[index] ?? hourly.swell_wave_height?.[index]),
    };
  }).filter((point) => !Number.isNaN(point.date.getTime()));
};

const getWindowBounds = (baseDate, startHour, endHour) => {
  const start = new Date(baseDate);
  start.setHours(startHour, 0, 0, 0);
  const end = new Date(baseDate);
  end.setHours(endHour, 0, 0, 0);
  if (endHour <= startHour) end.setDate(end.getDate() + 1);
  return { start, end };
};

const summarizePoints = (points) => {
  const temps = validNumbers(points.map((point) => point.temp));
  const rainValues = validNumbers(points.map((point) => point.rain));
  const uvValues = validNumbers(points.map((point) => point.uv));
  const cloudValues = validNumbers(points.map((point) => point.cloud));
  const winds = validNumbers(points.map((point) => point.windKt));
  const maxWind = winds.length ? Math.max(...winds) : null;
  const maxWindPoint = points.find((point) => Number.isFinite(point.windKt) && Math.abs(point.windKt - maxWind) < 0.01);

  return {
    minTemp: temps.length ? Math.min(...temps) : null,
    maxTemp: temps.length ? Math.max(...temps) : null,
    totalRain: rainValues.reduce((sum, value) => sum + value, 0),
    maxUv: uvValues.length ? Math.max(...uvValues) : null,
    avgCloud: cloudValues.length ? cloudValues.reduce((sum, value) => sum + value, 0) / cloudValues.length : null,
    maxWind,
    maxWindDirection: maxWindPoint?.windDirection,
  };
};

const buildTimeBlocks = (points) => {
  if (!points.length) return [];
  const baseDate = points[0].date;
  const windows = [
    { label: '06 - 10', ...getWindowBounds(baseDate, 6, 10) },
    { label: '10 - 14', ...getWindowBounds(baseDate, 10, 14) },
    { label: '14 - 18', ...getWindowBounds(baseDate, 14, 18) },
    { label: '18 - 22', ...getWindowBounds(baseDate, 18, 22) },
    { label: '22 - 06', ...getWindowBounds(baseDate, 22, 6) },
  ];

  return windows.map((windowInfo) => {
    const blockPoints = points.filter((point) => point.date >= windowInfo.start && point.date < windowInfo.end);
    const summary = summarizePoints(blockPoints);
    return {
      ...windowInfo,
      points: blockPoints,
      summary,
      icon: getWeatherIcon({ rain: summary.totalRain, cloud: summary.avgCloud, uv: summary.maxUv }),
    };
  }).filter((block) => block.points.length);
};

const interpolateThresholdTime = (pointA, pointB, threshold) => {
  const valueA = Number(pointA?.uv);
  const valueB = Number(pointB?.uv);
  if (!Number.isFinite(valueA) || !Number.isFinite(valueB) || valueA === valueB) return pointB?.date || null;
  const ratio = (threshold - valueA) / (valueB - valueA);
  const startTime = pointA.date?.getTime();
  const endTime = pointB.date?.getTime();
  if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) return pointB?.date || null;
  return new Date(startTime + ((endTime - startTime) * Math.min(1, Math.max(0, ratio))));
};

const getUvThresholdWindow = (points, threshold) => {
  let startsAt = null;
  let dropsAt = null;

  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    if (!Number.isFinite(point?.uv)) continue;
    if (point.uv >= threshold) {
      const previous = points[index - 1];
      startsAt = previous && Number.isFinite(previous.uv) && previous.uv < threshold
        ? interpolateThresholdTime(previous, point, threshold)
        : point.date;
      break;
    }
  }

  if (startsAt) {
    for (let index = 1; index < points.length; index += 1) {
      const previous = points[index - 1];
      const point = points[index];
      if (!Number.isFinite(previous?.uv) || !Number.isFinite(point?.uv)) continue;
      if (previous.uv >= threshold && point.uv < threshold && point.date > startsAt) {
        dropsAt = interpolateThresholdTime(previous, point, threshold);
        break;
      }
    }
  }

  return startsAt ? { startsAt, dropsAt } : null;
};

const getBaseChartOptions = (title, extra = {}) => {
  const { tooltip: tooltipOptions = {}, ...pluginOptions } = extra.plugins || {};
  const locationLabel = String(extra.locationLabel || '').trim();
  const tooltipCallbacks = tooltipOptions.callbacks || {};

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      title: { display: false, text: title },
      legend: { display: true, position: 'bottom', labels: { boxWidth: 10, usePointStyle: true } },
      ...pluginOptions,
      tooltip: {
        enabled: true,
        ...tooltipOptions,
        callbacks: {
          ...tooltipCallbacks,
          afterBody: (items) => {
            const existing = tooltipCallbacks.afterBody?.(items);
            const existingLines = Array.isArray(existing) ? existing : (existing ? [existing] : []);
            return locationLabel ? [...existingLines, `Location: ${locationLabel}`] : existingLines;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 6 },
        grid: { display: false },
      },
      ...(extra.scales || {}),
    },
  };
};

export default function WeatherForecastModal({
  open,
  onClose,
  weatherData,
  locationName,
  dateRange,
  stayWindow,
  itineraryContexts = [],
  desktopFullscreen = false,
  desktopViewportPercent = 100,
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedBlock, setExpandedBlock] = useState(null);
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const normalizedDesktopViewportPercent = Math.max(
    1,
    Math.min(100, Number(desktopViewportPercent) || 100)
  );

  useEffect(() => {
    if (!desktopFullscreen || !open || typeof document === 'undefined') return undefined;
    document.body.classList.add('WeatherDetailModalDesktopOpen');
    return () => document.body.classList.remove('WeatherDetailModalDesktopOpen');
  }, [desktopFullscreen, open]);

  const points = useMemo(() => buildHourlyPoints(weatherData), [weatherData]);
  const summary = useMemo(() => summarizePoints(points), [points]);
  const blocks = useMemo(() => buildTimeBlocks(points), [points]);
  const uvThresholdWindow = useMemo(() => getUvThresholdWindow(points, UV_ALERT_THRESHOLD), [points]);
  const currentBlockLabel = useMemo(() => (
    blocks.find((block) => currentTime >= block.start && currentTime < block.end)?.label || null
  ), [blocks, currentTime]);

  useEffect(() => {
    if (!open) return undefined;
    setCurrentTime(new Date());
    const timer = window.setInterval(() => setCurrentTime(new Date()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  useEffect(() => {
    if (open && currentBlockLabel) setExpandedBlock(currentBlockLabel);
  }, [currentBlockLabel, open]);

  const labels = points.map((point) => point.label);
  const icon = getWeatherIcon({ rain: summary.totalRain, cloud: summary.avgCloud, uv: summary.maxUv });
  const conditionLabel = makeConditionLabel(summary);
  const windLevel = getWindLevel(summary.maxWind || 0);
  const windDirection = getCardinalDirection(summary.maxWindDirection);

  const temperatureRainChart = useMemo(() => ({
    labels,
    datasets: [
      {
        type: 'line',
        label: 'Temperature',
        data: points.map((point) => Number.isFinite(point.temp) ? round(point.temp, 1) : null),
        borderColor: '#f97316',
        backgroundColor: 'rgba(249, 115, 22, 0.12)',
        yAxisID: 'y',
        tension: 0.25,
        pointRadius: 2,
      },
      {
        type: 'bar',
        label: 'Rain',
        data: points.map((point) => Number.isFinite(point.rain) ? round(point.rain, 1) : 0),
        backgroundColor: 'rgba(14, 165, 233, 0.45)',
        yAxisID: 'rain',
      },
    ],
  }), [labels, points]);

  const windWaveChart = useMemo(() => ({
    labels,
    datasets: [
      {
        type: 'line',
        label: 'Wind speed',
        data: points.map((point) => Number.isFinite(point.windKt) ? round(point.windKt, 1) : null),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.15)',
        tension: 0.2,
        pointRadius: 2,
        yAxisID: 'y',
      },
      {
        type: 'bar',
        label: 'Waves',
        data: points.map((point) => Number.isFinite(point.waveHeight) ? round(point.waveHeight, 1) : null),
        backgroundColor: 'rgba(6, 182, 212, 0.35)',
        yAxisID: 'waves',
      },
    ],
  }), [labels, points]);

  const cloudUvChart = useMemo(() => ({
    labels,
    datasets: [
      {
        type: 'line',
        label: 'Cloud cover',
        data: points.map((point) => Number.isFinite(point.cloud) ? round(point.cloud, 0) : null),
        borderColor: '#64748b',
        backgroundColor: 'rgba(100, 116, 139, 0.16)',
        tension: 0.2,
        pointRadius: 0,
        yAxisID: 'cloud',
      },
      {
        type: 'line',
        label: 'UV',
        data: points.map((point) => Number.isFinite(point.uv) ? round(point.uv, 1) : null),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.18)',
        tension: 0.2,
        pointRadius: 2,
        yAxisID: 'y',
      },
    ],
  }), [labels, points]);

  if (!weatherData) return null;

  const metricCards = [
    { key: 'temp', icon: 'thermometer half', label: 'Temperature', value: `${formatValue(summary.minTemp, '°C')} - ${formatValue(summary.maxTemp, '°C')}` },
    { key: 'wind', icon: 'location arrow', label: 'Wind', value: `${formatValue(summary.maxWind, ' kt')} ${windDirection}`, detail: windLevel.label },
    { key: 'rain', icon: 'tint', label: 'Rain', value: formatValue(summary.totalRain, ' mm'), detail: summary.totalRain >= 1 ? 'possible' : 'low' },
    { key: 'uv', icon: 'sun', label: 'UV', value: formatValue(summary.maxUv, ''), detail: summary.maxUv >= 8 ? 'very high' : summary.maxUv >= 6 ? 'elevated' : 'moderate' },
    { key: 'cloud', icon: 'cloud', label: 'Cloud cover', value: formatValue(summary.avgCloud, '%') },
  ];

  const renderChart = () => {
    if (!points.length) {
      return <div style={{ color: '#64748b', padding: 12 }}>Hourly forecast details are not available.</div>;
    }
    if (activeTab === 'wind') {
      return (
        <Line
          data={windWaveChart}
          options={getBaseChartOptions('Wind & waves', {
            locationLabel: locationName,
            plugins: {
              chartAreaBands: {
                bands: [
                  { from: 0, to: 10, color: 'rgba(22, 163, 74, 0.08)' },
                  { from: 10, to: 15, color: 'rgba(249, 115, 22, 0.08)' },
                  { from: 15, to: 25, color: 'rgba(239, 68, 68, 0.08)' },
                  { from: 25, to: Math.max(30, (summary.maxWind || 0) + 3), color: 'rgba(220, 38, 38, 0.10)' },
                ],
              },
            },
            scales: {
              y: { title: { display: true, text: 'kt' }, beginAtZero: true },
              waves: { position: 'right', title: { display: true, text: 'm' }, beginAtZero: true, grid: { drawOnChartArea: false } },
            },
          })}
          plugins={[chartAreaBandsPlugin]}
        />
      );
    }
    if (activeTab === 'rainUv') {
      return (
        <Line
          data={cloudUvChart}
          options={getBaseChartOptions('Cloud & UV', {
            locationLabel: locationName,
            plugins: {
              uvThresholdFill: {
                threshold: UV_ALERT_THRESHOLD,
                color: 'rgba(239, 68, 68, 0.22)',
              },
            },
            scales: {
              y: { title: { display: true, text: 'UV' }, beginAtZero: true },
              cloud: { position: 'right', title: { display: true, text: '%' }, min: 0, max: 100, grid: { drawOnChartArea: false } },
            },
          })}
          plugins={[uvThresholdFillPlugin]}
        />
      );
    }
    return (
      <Line
        data={temperatureRainChart}
        options={getBaseChartOptions('Temperature & rain', {
          locationLabel: locationName,
          scales: {
            y: { title: { display: true, text: '°C' } },
            rain: { position: 'right', title: { display: true, text: 'mm' }, beginAtZero: true, grid: { drawOnChartArea: false } },
          },
        })}
      />
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="small"
      className={`WeatherDetailModal${desktopFullscreen ? ' WeatherDetailModal--desktop' : ''}`}
      style={desktopFullscreen ? {
        width: `${normalizedDesktopViewportPercent}vw`,
        maxWidth: 'none',
        height: `calc(var(--vh, 1vh) * ${normalizedDesktopViewportPercent})`,
        margin: 0,
      } : { maxWidth: 620 }}
    >
      <style>
        {`
          .WeatherDetailModal.ui.modal {
            border-radius: 18px;
            overflow: hidden;
          }
          @media (min-width: 768px) {
            .WeatherDetailModalDesktopOpen .TourUser-lobby-topbar {
              z-index: 900 !important;
              filter: brightness(0.45);
              pointer-events: none;
            }
            .WeatherDetailModal--desktop.ui.modal {
              z-index: 2001 !important;
            }
            .WeatherDetailModal--desktop.ui.modal > .content {
              height: 100%;
            }
            .WeatherDetailModal--desktop .WeatherDetailModal-scroll {
              display: flex;
              flex-direction: column;
              height: 100%;
              overflow: hidden !important;
            }
            .WeatherDetailModal--desktop .WeatherDetailModal-body {
              grid-template-columns: repeat(5, minmax(0, 1fr));
              grid-auto-rows: min-content;
              align-content: start;
              flex: 1 1 auto;
              min-height: 0;
              overflow-y: auto;
              padding: 16px 20px 18px;
            }
            .WeatherDetailModal--desktop .WeatherDetailModal-hero,
            .WeatherDetailModal--desktop .WeatherDetailModal-metrics,
            .WeatherDetailModal--desktop .WeatherDetailModal-desktop-lower,
            .WeatherDetailModal--desktop .WeatherDetailModal-explanation {
              grid-column: 1 / -1;
            }
            .WeatherDetailModal--desktop .WeatherDetailModal-metrics {
              grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
            }
            .WeatherDetailModal--desktop .WeatherDetailModal-metric {
              min-height: 78px !important;
              padding: 10px 12px !important;
            }
            .WeatherDetailModal--desktop .WeatherDetailModal-desktop-lower {
              display: grid;
              grid-template-columns: minmax(380px, 0.9fr) minmax(500px, 1.1fr);
              gap: 12px;
            }
            .WeatherDetailModal--desktop .WeatherDetailModal-chart {
              min-width: 0;
            }
            .WeatherDetailModal--desktop .WeatherDetailModal-chart-canvas {
              height: 250px !important;
            }
            .WeatherDetailModal--desktop .WeatherDetailModal-explanation {
              display: none;
            }
          }
          @media (max-width: 767px) {
            .WeatherDetailModal.ui.modal {
              width: 100vw !important;
              height: calc(var(--vh, 1vh) * 100) !important;
              max-width: none !important;
              margin: 0 !important;
              top: 0 !important;
              left: 0 !important;
              border-radius: 0 !important;
            }
            .WeatherDetailModal .content {
              height: calc(var(--vh, 1vh) * 100);
            }
          }
        `}
      </style>
      <ModalContent style={{ padding: 0, background: '#f8fafc' }}>
        <div className="WeatherDetailModal-scroll" style={{ height: '100%', maxHeight: 'calc(var(--vh, 1vh) * 100)', overflowY: 'auto' }}>
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 3,
              background: 'rgba(255, 255, 255, 0.96)',
              backdropFilter: 'blur(10px)',
              borderBottom: '1px solid #e2e8f0',
              padding: '14px 16px 12px',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '38px 1fr', gap: 10, alignItems: 'center' }}>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close weather details"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: '1px solid #dbeafe',
                  background: '#ffffff',
                  color: '#0b6fcf',
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: '0 4px 14px rgba(15, 23, 42, 0.10)',
                  cursor: 'pointer',
                }}
              >
                <Icon name="chevron left" style={{ margin: 0 }} />
              </button>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 900, color: '#0b6fcf', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Weather
                </div>
                <div style={{ fontSize: 20, lineHeight: 1.15, fontWeight: 900, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {locationName || 'Forecast'}
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{dateRange}</div>
              </div>
            </div>
          </div>

          <div className="WeatherDetailModal-body" style={{ padding: 14, display: 'grid', gap: 12 }}>
            <section
              className="WeatherDetailModal-hero"
              style={{
                borderRadius: 18,
                padding: 16,
                color: '#ffffff',
                background: 'linear-gradient(135deg, #075985 0%, #0b6fcf 55%, #38bdf8 100%)',
                boxShadow: '0 14px 34px rgba(14, 116, 144, 0.24)',
              }}
            >
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <div
                  style={{
                    width: 58,
                    height: 58,
                    borderRadius: 18,
                    background: 'rgba(255,255,255,0.16)',
                    display: 'grid',
                    placeItems: 'center',
                    flex: '0 0 auto',
                  }}
                >
                  <Icon name={icon.name} style={{ color: '#fbbf24', fontSize: 30, margin: 0 }} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 22, lineHeight: 1.1, fontWeight: 900 }}>{conditionLabel}</div>
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '7px 12px', color: '#dff6ff', fontWeight: 700 }}>
                    <span>{formatValue(summary.minTemp, '°C')} - {formatValue(summary.maxTemp, '°C')}</span>
                    <span>{formatValue(summary.maxWind, ' kt')} {windDirection}</span>
                    <span>UV {formatValue(summary.maxUv)}</span>
                    <span>{formatValue(summary.totalRain, ' mm')} rain</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="WeatherDetailModal-metrics" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
              {metricCards.map((metric) => (
                <div
                  key={metric.key}
                  className="WeatherDetailModal-metric"
                  style={{
                    borderRadius: 14,
                    border: '1px solid #dbeafe',
                    background: '#ffffff',
                    padding: '11px 12px',
                    minHeight: 86,
                  }}
                >
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#64748b', fontSize: 12, fontWeight: 800 }}>
                    <Icon name={metric.icon} style={{ color: '#0b6fcf', margin: 0 }} />
                    {metric.label}
                  </div>
                  <div style={{ marginTop: 9, color: '#0f172a', fontSize: 20, lineHeight: 1.05, fontWeight: 900 }}>{metric.value}</div>
                  {metric.detail ? <div style={{ marginTop: 4, color: '#64748b', fontSize: 12 }}>{metric.detail}</div> : null}
                </div>
              ))}
            </section>

            <div className="WeatherDetailModal-desktop-lower">
            <section style={{ borderRadius: 16, border: '1px solid #dbeafe', background: '#ffffff', padding: 12 }}>
              <div style={{ fontWeight: 900, color: '#0f172a', marginBottom: 10 }}>Hourly forecast</div>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(blocks.length || 1, 5)}, minmax(0, 1fr))`, gap: 0 }}>
                {blocks.map((block, index) => {
                  const isCurrentBlock = currentBlockLabel === block.label;
                  const isActiveBlock = expandedBlock === block.label;
                  return (
                  <button
                    key={block.label}
                    type="button"
                    onClick={() => setExpandedBlock(expandedBlock === block.label ? null : block.label)}
                    title={isCurrentBlock ? 'Current time' : undefined}
                    aria-current={isCurrentBlock ? 'time' : undefined}
                    style={{
                      border: 0,
                      borderLeft: index === 0 ? 0 : '1px solid #e2e8f0',
                      background: isActiveBlock ? '#eff6ff' : '#ffffff',
                      boxShadow: isCurrentBlock ? 'inset 0 3px 0 #0b6fcf' : 'none',
                      padding: '6px 4px',
                      display: 'grid',
                      gap: 5,
                      justifyItems: 'center',
                      color: '#0f172a',
                      cursor: 'pointer',
                      minWidth: 0,
                    }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 800, color: isCurrentBlock ? '#0b6fcf' : '#475569' }}>
                      {block.label}
                    </span>
                    <Icon name={block.icon.name} style={{ color: block.icon.color, margin: 0, fontSize: 18 }} />
                    <span style={{ fontSize: 15, lineHeight: 1.05, fontWeight: 900 }}>
                      {formatValue(block.summary.minTemp, '°')}<br />{formatValue(block.summary.maxTemp, '°')}
                    </span>
                    <span style={{ fontSize: 11, color: '#475569' }}>{formatValue(block.summary.maxWind, ' kt')}</span>
                    <span style={{ fontSize: 11, color: '#64748b' }}>{block.summary.totalRain > 0 ? formatValue(block.summary.totalRain, ' mm') : '-'}</span>
                  </button>
                  );
                })}
              </div>
              {expandedBlock ? (
                <div style={{ marginTop: 10, padding: 10, borderRadius: 12, background: '#f8fafc', color: '#334155', fontSize: 13 }}>
                  {(() => {
                    const block = blocks.find((item) => item.label === expandedBlock);
                    if (!block) return null;
                    const itineraryMessages = getBlockItineraryMessages(block, itineraryContexts);
                    return (
                      <div style={{ display: 'grid', gap: 4 }}>
                        <strong style={{ color: '#0f172a' }}>{formatBlockTimeRange(block)}</strong>
                        {itineraryMessages.length ? (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, color: '#0b6fcf', fontWeight: 700 }}>
                            <Icon name='map marker alternate' style={{ margin: '1px 0 0', flex: '0 0 auto' }} />
                            <span>{itineraryMessages.join(' · ')}</span>
                          </div>
                        ) : null}
                        <span>Temperature {formatValue(block.summary.minTemp, '°C')} - {formatValue(block.summary.maxTemp, '°C')}</span>
                        <span>Wind up to {formatValue(block.summary.maxWind, ' kt')} {getCardinalDirection(block.summary.maxWindDirection)}</span>
                        <span>Rain {formatValue(block.summary.totalRain, ' mm')}</span>
                        <span>UV max {formatValue(block.summary.maxUv)}</span>
                        <span>Cloud cover {formatValue(block.summary.avgCloud, '%')}</span>
                      </div>
                    );
                  })()}
                </div>
              ) : null}
            </section>

            <section className="WeatherDetailModal-chart" style={{ borderRadius: 16, border: '1px solid #dbeafe', background: '#ffffff', padding: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6, marginBottom: 12 }}>
                {[
                  ['overview', 'Temp & rain'],
                  ['wind', 'Wind & waves'],
                  ['rainUv', 'Cloud & UV'],
                ].map(([key, label]) => (
                  <Button
                    key={key}
                    size="small"
                    compact
                    primary={activeTab === key}
                    basic={activeTab !== key}
                    onClick={() => setActiveTab(key)}
                    style={{ margin: 0, borderRadius: 10 }}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              {activeTab === 'rainUv' ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginBottom: 10,
                    padding: '8px 10px',
                    borderRadius: 10,
                    background: uvThresholdWindow ? 'rgba(254, 226, 226, 0.8)' : '#f8fafc',
                    color: uvThresholdWindow ? '#991b1b' : '#64748b',
                    fontSize: 12,
                    fontWeight: 800,
                  }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: 'rgba(239, 68, 68, 0.55)',
                      boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.16)',
                      flex: '0 0 auto',
                    }}
                  />
                  {uvThresholdWindow ? (
                    <span>
                      UV over {UV_ALERT_THRESHOLD.toFixed(1)} from {formatClock(uvThresholdWindow.startsAt)}
                      {uvThresholdWindow.dropsAt ? ` to ${formatClock(uvThresholdWindow.dropsAt)}` : ' onward'}
                    </span>
                  ) : (
                    <span>UV stays below {UV_ALERT_THRESHOLD.toFixed(1)} today</span>
                  )}
                </div>
              ) : null}
              <div className="WeatherDetailModal-chart-canvas" style={{ height: 235 }}>
                {renderChart()}
              </div>
            </section>

            </div>

            <section className="WeatherDetailModal-explanation" style={{ borderRadius: 16, border: '1px solid #dbeafe', background: '#ffffff', overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setExplanationOpen((value) => !value)}
                style={{
                  width: '100%',
                  border: 0,
                  background: '#ffffff',
                  padding: 13,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: '#0f172a',
                  fontWeight: 900,
                  cursor: 'pointer',
                }}
              >
                <span>How to read this forecast</span>
                <Icon name={explanationOpen ? 'chevron up' : 'chevron down'} style={{ margin: 0 }} />
              </button>
              {explanationOpen ? (
                <div style={{ borderTop: '1px solid #e2e8f0', padding: 13, color: '#475569', fontSize: 13, lineHeight: 1.45 }}>
                  <p style={{ marginTop: 0 }}><strong>Temperature & rain:</strong> temperature line with rain bars, useful for clothing and timing.</p>
                  <p><strong>Wind & waves:</strong> wind speed in knots with waves in metres when marine forecast data is available.</p>
                  <p style={{ marginBottom: 0 }}><strong>Cloud & UV:</strong> cloud cover percentage with UV intensity. Red translucent fill marks UV above {UV_ALERT_THRESHOLD.toFixed(1)}.</p>
                </div>
              ) : null}
            </section>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
